import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Inbox, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../api";
import { useAdminAuth } from "../AdminAuthContext";
import {
    Panel, PageHead, Loading, LoadError, EmptyState, StatusBadge, PriorityBadge,
    STATUS_LABELS, CASE_STATUSES, PRIORITIES, ENQUIRY_SOURCES, SOURCE_LABELS, fmtDate,
} from "../ui";

const SELECT =
    "rounded-lg border border-[#1D322E] bg-[#0E1A17] px-3 py-2 text-xs text-[#F7F5EF] outline-none focus:border-[#10B981] transition-colors";

export const AdminCases = () => {
    const { user } = useAdminAuth();
    const isAdmin = user?.role === "ADMIN";

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState({ status: "", specialty: "", country: "", priority: "", coordinator: "", source: "" });
    const [sort, setSort] = useState("created_at");
    const [order, setOrder] = useState("desc");
    const [coordinators, setCoordinators] = useState([]);
    const [data, setData] = useState(null);
    const [page, setPage] = useState(1);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!isAdmin) return;
        api.get("/admin/coordinators")
            .then((res) => setCoordinators(res.data.items))
            .catch(() => {});
    }, [isAdmin]);

    useEffect(() => {
        const t = setTimeout(() => {
            setLoading(true);
            const params = new URLSearchParams({ page: String(page), limit: "12", sort, order });
            if (search.trim()) params.set("search", search.trim());
            Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
            api.get(`/admin/cases?${params.toString()}`)
                .then((res) => {
                    setData(res.data);
                    setError("");
                })
                .catch(() => setError("Could not load cases. Please refresh."))
                .finally(() => setLoading(false));
        }, 250);
        return () => clearTimeout(t);
    }, [search, filters, page, sort, order]);

    const setFilter = (key, value) => {
        setFilters((f) => ({ ...f, [key]: value }));
        setPage(1);
    };

    const items = data?.items || [];

    return (
        <div data-testid="admin-cases-page">
            <PageHead title="Cases Registry" subtitle="Every enquiry becomes a case with a permanent reference." />

            <div className="flex flex-wrap items-center gap-3" data-testid="cases-filter-bar">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64716D]" />
                    <input
                        data-testid="cases-search-input"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                        placeholder="Search case ID, patient, email or phone…"
                        className="w-full rounded-lg border border-[#1D322E] bg-[#0E1A17] pl-9 pr-4 py-2 text-sm text-[#F7F5EF] placeholder-[#64716D] outline-none focus:border-[#10B981] transition-colors"
                    />
                </div>
                <select data-testid="cases-filter-status" value={filters.status} onChange={(e) => setFilter("status", e.target.value)} className={SELECT} aria-label="Filter by status">
                    <option value="">All statuses</option>
                    {CASE_STATUSES.map((s) => (
                        <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                    ))}
                </select>
                <select data-testid="cases-filter-priority" value={filters.priority} onChange={(e) => setFilter("priority", e.target.value)} className={SELECT} aria-label="Filter by priority">
                    <option value="">All priorities</option>
                    {PRIORITIES.map((p) => (
                        <option key={p} value={p}>{p}</option>
                    ))}
                </select>
                <select data-testid="cases-filter-source" value={filters.source} onChange={(e) => setFilter("source", e.target.value)} className={SELECT} aria-label="Filter by source">
                    <option value="">All sources</option>
                    {ENQUIRY_SOURCES.map((s) => (
                        <option key={s} value={s}>{SOURCE_LABELS[s]}</option>
                    ))}
                </select>
                {isAdmin && (
                    <select data-testid="cases-filter-coordinator" value={filters.coordinator} onChange={(e) => setFilter("coordinator", e.target.value)} className={SELECT} aria-label="Filter by coordinator">
                        <option value="">All coordinators</option>
                        <option value="unassigned">Unassigned</option>
                        {coordinators.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                )}
                <input
                    data-testid="cases-filter-country"
                    value={filters.country}
                    onChange={(e) => setFilter("country", e.target.value)}
                    placeholder="Country"
                    aria-label="Filter by country"
                    className={`${SELECT} w-28`}
                />
                <select data-testid="cases-sort" value={`${sort}:${order}`} onChange={(e) => { const [s, o] = e.target.value.split(":"); setSort(s); setOrder(o); setPage(1); }} className={SELECT} aria-label="Sort cases">
                    <option value="created_at:desc">Newest first</option>
                    <option value="created_at:asc">Oldest first</option>
                    <option value="updated_at:desc">Recently updated</option>
                    <option value="patient_name:asc">Patient A–Z</option>
                    <option value="priority:desc">Priority</option>
                    <option value="status:asc">Status</option>
                </select>
            </div>

            {error ? (
                <div className="mt-6"><LoadError message={error} /></div>
            ) : loading && !data ? (
                <Loading />
            ) : (
                <Panel className="mt-6 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left" data-testid="cases-table">
                            <thead>
                                <tr className="border-b border-[#1A2E2A] bg-[#0E1A17]">
                                    {["Case", "Patient", "Country", "Specialty", "Status", "Priority", "Coordinator", "Created", ""].map((h) => (
                                        <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#64716D] whitespace-nowrap">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((c) => (
                                    <tr
                                        key={c.case_id}
                                        data-testid={`case-row-${c.case_id}`}
                                        className="border-b border-[#1A2E2A] hover:bg-[#172A26] transition-colors"
                                    >
                                        <td className="px-4 py-3 text-xs font-mono tracking-tight text-emerald-300 whitespace-nowrap">{c.case_id}</td>
                                        <td className="px-4 py-3 text-sm text-[#F7F5EF] font-medium whitespace-nowrap">
                                            {c.patient_name}
                                            <span className="block text-xs text-[#64716D] font-normal">{c.email}</span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-[#A3B8B0] whitespace-nowrap">{c.country || "—"}</td>
                                        <td className="px-4 py-3 text-sm text-[#A3B8B0] whitespace-nowrap">{c.specialty || "—"}</td>
                                        <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                                        <td className="px-4 py-3"><PriorityBadge priority={c.priority} /></td>
                                        <td className="px-4 py-3 text-sm text-[#A3B8B0] whitespace-nowrap">
                                            {c.assigned_coordinator ? c.assigned_coordinator.name : <span className="text-amber-400">Unassigned</span>}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-[#64716D] whitespace-nowrap">{fmtDate(c.created_at)}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <Link
                                                to={`/admin/cases/${c.case_id}`}
                                                data-testid={`case-open-${c.case_id}`}
                                                className="text-xs font-semibold text-emerald-300 hover:text-emerald-200"
                                            >
                                                Open →
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {items.length === 0 && (
                        <EmptyState
                            icon={Inbox}
                            title="No cases match these filters"
                            hint="Try clearing the search or choosing different filters."
                        />
                    )}
                    <div className="flex items-center justify-between px-4 py-3 border-t border-[#1A2E2A]" data-testid="cases-pagination">
                        <p className="text-xs text-[#64716D]" data-testid="cases-total-count">
                            {data?.total ?? 0} case{(data?.total ?? 0) === 1 ? "" : "s"} · page {data?.page ?? 1} of {data?.pages ?? 1}
                        </p>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                data-testid="cases-pagination-prev"
                                disabled={(data?.page ?? 1) <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                className="rounded-lg border border-[#1D322E] p-2 text-[#A3B8B0] hover:text-[#F7F5EF] hover:border-[#0B5148] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Previous page"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                data-testid="cases-pagination-next"
                                disabled={(data?.page ?? 1) >= (data?.pages ?? 1)}
                                onClick={() => setPage((p) => p + 1)}
                                className="rounded-lg border border-[#1D322E] p-2 text-[#A3B8B0] hover:text-[#F7F5EF] hover:border-[#0B5148] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Next page"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </Panel>
            )}
        </div>
    );
};
